import { Helmet } from 'react-helmet-async';
import { filter } from 'lodash';
import React, { useState, useRef } from 'react';

import {
  Card,
  Table,
  Popover,
  TableRow,
  MenuItem,
  TableBody,
  TableCell,
  Container,
  TableContainer,
  TablePagination,
} from '@mui/material';
import { getToken } from '../utils/common';
import logger from '../utils/logger';

import Iconify from '../components/iconify';
import Scrollbar from '../components/scrollbar';
import ErrorBoundary from '../components/error-boundary/ErrorBoundary';

import { UserListHead, UserListToolbar } from '../sections/@dashboard/user';

const TABLE_HEAD = [
  { id: '', label: 'Date & Time', alignRight: false },
  { id: '', label: 'User', alignRight: false },
  { id: '', label: 'Role', alignRight: false },
  { id: '', label: 'Action', alignRight: false },
];

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator, query) {
  const stabilizedThis = array?.map((el, index) => [el, index]);
  stabilizedThis?.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  if (query) {
    return filter(array, (_user) => _user.name.toLowerCase().indexOf(query.toLowerCase()) !== -1);
  }
  return stabilizedThis?.map((el) => el[0]);
}

export default function UserPage(props) {
  const [open, setOpen] = useState(null);
  const [order, setOrder] = useState('desc');
  const [selected] = useState([]);
  const [orderBy, setOrderBy] = useState('timestamp');
  const [filterName, setFilterName] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [data, setData] = useState([]);
  const [total, settotal] = useState(1);
  const [page, setpage] = useState(0);
  const [loading, setLoading] = useState(false);

  // Use ref to track current rows per page for numbering calculation
  const rowsPerPageRef = useRef(20);

  const handleCloseMenu = () => {
    setOpen(null);
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);

    let sort = '';
    if (property === 'Date & Time') {
      sort = 'timestamp';
    } else if (property === 'User') {
      sort = 'identifier';
    } else if (property === 'Role') {
      sort = 'role';
    } else if (property === 'Action') {
      sort = 'message';
    }

    // Reset to first page when sorting
    setpage(0);

    // Build URL with proper parameters
    const url = new URL(`${globalThis.REACT_APP_API_PURE}activity/list`);
    url.searchParams.set('page', '1');
    url.searchParams.set('limit', rowsPerPage.toString());
    url.searchParams.set('sort', sort);
    url.searchParams.set('order', isAsc ? 'desc' : 'asc');

    // Add filter parameters if filterName exists
    if (filterName) {
      url.searchParams.set('message', filterName);
      url.searchParams.set('type', filterName);
      url.searchParams.set('identifier', filterName);
    }

    fetchInfo(url.toString(), 0, rowsPerPage);
  };

  const handleChangePage = (event) => {
    // Clear data immediately to prevent stale data display
    setData([]);
    setLoading(true);

    // Update state immediately
    setpage(event);
    rowsPerPageRef.current = rowsPerPage; // Ensure ref is in sync

    // Build URL with proper parameters
    const url = new URL(`${globalThis.REACT_APP_API_PURE}activity/list`);
    url.searchParams.set('page', (event + 1).toString());
    url.searchParams.set('limit', rowsPerPage.toString());
    url.searchParams.set('sort', orderBy);
    url.searchParams.set('order', order);

    // Add filter parameters if filterName exists
    if (filterName) {
      url.searchParams.set('message', filterName);
      url.searchParams.set('type', filterName);
      url.searchParams.set('identifier', filterName);
    }

    fetchInfo(url.toString(), event, rowsPerPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = Number.parseInt(event.target.value, 10);

    // Clear data immediately to prevent stale data display
    setData([]);
    setLoading(true);

    // Keep the same page number, just change rows per page
    setRowsPerPage(newRowsPerPage);
    rowsPerPageRef.current = newRowsPerPage;

    // Build URL with proper parameters - keep the same page
    const url = new URL(`${globalThis.REACT_APP_API_PURE}activity/list`);
    url.searchParams.set('limit', newRowsPerPage.toString());
    url.searchParams.set('page', (page + 1).toString()); // Keep current page (convert to 1-based for API)
    url.searchParams.set('sort', orderBy);
    url.searchParams.set('order', order);

    // Add filter parameters if filterName exists
    if (filterName) {
      url.searchParams.set('message', filterName);
      url.searchParams.set('type', filterName);
      url.searchParams.set('identifier', filterName);
    }

    fetchInfo(url.toString(), page, newRowsPerPage);
  };

  const handleFilterByName = (event) => {
    const filterValue = event.target.value;
    setFilterName(filterValue);
    setpage(0); // Reset page when filtering

    // Build URL with proper parameters
    const url = new URL(`${globalThis.REACT_APP_API_PURE}activity/list`);
    url.searchParams.set('page', '1');
    url.searchParams.set('limit', rowsPerPage.toString());
    url.searchParams.set('sort', orderBy);
    url.searchParams.set('order', order);

    // Add filter parameters if filterValue exists
    if (filterValue) {
      url.searchParams.set('message', filterValue);
      url.searchParams.set('type', filterValue);
      url.searchParams.set('identifier', filterValue);
    }

    fetchInfo(url.toString(), 0, rowsPerPage);
  };

  const fetchInfo = (url, currentPage, currentRowsPerPage) => {
    setLoading(true);

    return fetch(url, {
      headers: {
        Authorization: `Bearer ${getToken()}`,
      },
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        return res.json();
      })
      .then((d) => {
        // Handle API response structure: { data: [...], total: number, page: number, limit: number, start_from: number }
        const responseData = d.data || d.Activity || [];
        const responseTotal = d.total || d.Total || 0;

        // Validate response data
        if (!Array.isArray(responseData)) {
          logger.warn('Invalid response data format:', responseData);
          setData([]);
        } else {
          setData(responseData);
        }

        settotal(Math.max(0, responseTotal));
        setLoading(false);
      })
      .catch((error) => {
        logger.error('Error fetching activity data:', error);
        setData([]);
        settotal(0);
        setLoading(false);

        // Show user-friendly error message
        if (error.message.includes('401')) {
          logger.warn('Authentication failed. Please login again.');
        } else if (error.message.includes('403')) {
          logger.warn('Access denied. Insufficient permissions.');
        } else if (error.message.includes('500')) {
          logger.error('Server error. Please try again later.');
        }
      });
  };
  React.useEffect(() => {
    setpage(0); // Initialize page to 0

    // Build URL with proper parameters
    const url = new URL(`${globalThis.REACT_APP_API_PURE}activity/list`);
    url.searchParams.set('page', '1');
    url.searchParams.set('limit', rowsPerPage.toString());
    url.searchParams.set('sort', orderBy);
    url.searchParams.set('order', order);

    fetchInfo(url.toString(), 0, rowsPerPage);
  }, []);

  // Keep ref in sync with state
  React.useEffect(() => {
    rowsPerPageRef.current = rowsPerPage;
  }, [rowsPerPage]);

  return (
    <ErrorBoundary>
      <Helmet>
        <title> Activity Log | SMARTSHIELD </title>
      </Helmet>
      <Container>
        <Card>
          <UserListToolbar numSelected={selected.length} filterName={filterName} onFilterName={handleFilterByName} />
          <Scrollbar>
            <TableContainer sx={{ minWidth: 800, borderRadius: 2, border: '1px solid #e0e0e0' }}>
              <Table>
                <UserListHead
                  order={order}
                  hideSortIcon={false}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  rowCount={data?.length}
                  numSelected={selected?.length}
                  onRequestSort={handleRequestSort}
                  assign="label"
                />
                <TableBody>
                  {data?.map((item, itemIndex) => {
                    const inputTimestamp = item.timestamp;
                    const dateTime = new Date(inputTimestamp);
                    dateTime.setUTCHours(dateTime.getUTCHours());

                    const year = dateTime.getFullYear();
                    const month = String(dateTime.getMonth() + 1).padStart(2, '0');
                    const day = String(dateTime.getDate()).padStart(2, '0');
                    const hours = String(dateTime.getHours()).padStart(2, '0');
                    const minutes = String(dateTime.getMinutes()).padStart(2, '0');
                    const formattedDateTime = `${year}-${month}-${day} ${hours}:${minutes}`;

                    // Use row number provided by backend API
                    const rowNumber = item.row_number || page * rowsPerPage + itemIndex + 1;

                    return (
                      <TableRow
                        hover
                        key={`activity-${item.timestamp}-${itemIndex}-${item.identifier}`}
                        sx={{
                          '&:last-child td, &:last-child th': { border: 0 },
                        }}
                      >
                        <TableCell align="left" sx={{ maxWidth: 20 }}>
                          {rowNumber}
                        </TableCell>
                        <TableCell component="th" scope="row" sx={{ minWidth: 80, maxWidth: 80 }}>
                          {formattedDateTime}
                        </TableCell>
                        <TableCell component="th" scope="row" sx={{ minWidth: 180, maxWidth: 180 }}>
                          {item.identifier}
                        </TableCell>
                        <TableCell component="th" scope="row" sx={{ minWidth: 80, maxWidth: 80 }}>
                          {item.role}
                        </TableCell>
                        <TableCell component="th" scope="row" sx={{ maxWidth: 300, minWidth: 300 }}>
                          {item.message}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>

          {loading && <div style={{ textAlign: 'center', padding: '20px' }}>Loading...</div>}

          <TablePagination
            rowsPerPageOptions={[20, 50, 100]}
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(event, value) => handleChangePage(value)}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>
      </Container>
      <Popover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              p: 1,
              width: 140,
              '& .MuiMenuItem-root': {
                px: 1,
                typography: 'body2',
                borderRadius: 0.75,
              },
            },
          },
        }}
      >
        <MenuItem>
          <Iconify icon={'eva:edit-fill'} sx={{ mr: 2 }} />
          Edit
        </MenuItem>

        <MenuItem sx={{ color: 'error.main' }}>
          <Iconify icon={'eva:trash-2-outline'} sx={{ mr: 2 }} />
          Delete
        </MenuItem>
      </Popover>
    </ErrorBoundary>
  );
}
